// App.js - Con Sistema Multi-Tenant Attivo - VERSIONE FINALE CORRETTA + NUOVI FRONTEND AI
import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline, Box } from "@mui/material";

// Context Provider
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DomainProvider, useDomain, useCourtesyPage } from "./context/DomainContext";

// Routing helpers
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";

// UI Components
import MainNavbar from "./components/MainNavbar";
import CourtesyPage from "./components/CourtesyPage";
import LoadingScreen from "./components/LoadingScreen";
import ProjectAccessGuard from "./components/ProjectAccessGuard";

// Pagine base (import diretti per componenti essenziali)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Onboarding from "./pages/Onboarding";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";

// Admin di Sistema (lazy loading per performance)
const GestioneProgetti = React.lazy(() => import("./pages/GestioneProgetti"));
const GestioneUtentiPiattaforma = React.lazy(() => import("./pages/GestioneUtenti"));
const GestioneTemplates = React.lazy(() => import("./pages/GestioneTemplates"));
const GestionePolicySistema = React.lazy(() => import("./pages/GestioneAgents"));
const GestioneAgentiBlueprintSistema = React.lazy(() => import("./pages/GestioneBlueprints"));

// Project Agents Structure Management
const ProjectAgentsStructure = React.lazy(() => 
  import("./pages/pro_agents_structure_ux").catch(() => {
    console.error("ProjectAgentsStructure non trovato");
    return { default: () => <ComponentNotFound name="Project Agents Structure" /> };
  })
);

// Dashboard struttura progetti / piattaforma (lazy loading)
const ProjectDashboard = React.lazy(() => import("./pages/ProjectDashboard"));
const SystemDashboard = React.lazy(() => import("./pages/SystemDashboard"));

// Pagine Utente/AI e test (lazy loading)
const CreaImpronta = React.lazy(() => import("./pages/CreaImpronta"));
const CreaImprontaUp = React.lazy(() => import("./pages/CreaImprontaUp"));
const FileManager = React.lazy(() => import("./pages/FileManager"));
const AgentChatPage = React.lazy(() => import("./pages/AgentChatPage"));
const Agent_call_ux = React.lazy(() => import("./pages/Agent_call_ux"));
const ArchivioRisonanze = React.lazy(() => import("./components/ArchivioRisonanze"));
const JustGo = React.lazy(() => import("./pages/JustGo"));

// Email & onboarding gate
import VerifyEmailPage from "./pages/VerifyEmailPage";
import OnboardingGate from "./components/OnboardingGate";

// Componenti avanzati (tutti con lazy loading e gestione errori)
const AgentBuilder = React.lazy(() => 
  import("./components/AgentBuilder").catch(() => {
    console.error("AgentBuilder non trovato");
    return { default: () => <ComponentNotFound name="AgentBuilder" /> };
  })
);

const AgentCreator = React.lazy(() => 
  import("./pages/agent_creator").catch(() => {
    console.error("AgentCreator non trovato");
    return { default: () => <ComponentNotFound name="AgentCreator" /> };
  })
);

const AgentChatPageCommittee = React.lazy(() => 
  import("./pages/AgentChatPage_committee").catch(() => {
    console.error("AgentChatPage_committee non trovato");
    return { default: () => <ComponentNotFound name="Committee Chat" /> };
  })
);

const AgentBattleArena = React.lazy(() => 
  import("./pages/AgentBattleArena").catch(() => {
    console.error("AgentBattleArena non trovato");
    return { default: () => <ComponentNotFound name="Battle Arena" /> };
  })
);

const Aicommitteeroom = React.lazy(() => 
  import("./pages/Aicommitteeroom").catch(() => {
    console.error("Aicommitteeroom non trovato");
    return { default: () => <ComponentNotFound name="AI Committee Room" /> };
  })
);

// === NUOVI FRONTEND AI INNOVATIVI ===
const AIWorkspaceCollaborativo = React.lazy(() => 
  import("./pages/AIWorkspaceCollaborativo").catch(() => {
    console.error("AIWorkspaceCollaborativo non trovato");
    return { default: () => <ComponentNotFound name="AI Workspace Collaborativo" /> };
  })
);

const DocumentIntelligenceStudio = React.lazy(() => 
  import("./pages/DocumentIntelligenceStudio").catch(() => {
    console.error("DocumentIntelligenceStudio non trovato");
    return { default: () => <ComponentNotFound name="Document Intelligence Studio" /> };
  })
);

const AIAgentOrchestrator = React.lazy(() => 
  import("./pages/AIAgentOrchestrator").catch(() => {
    console.error("AIAgentOrchestrator non trovato");
    return { default: () => <ComponentNotFound name="AI Agent Orchestrator" /> };
  })
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <h2>Oops! Qualcosa è andato storto</h2>
          <p>{this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>
            Ricarica la pagina
          </button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Componente placeholder per moduli mancanti
const ComponentNotFound = ({ name }) => (
  <Box sx={{ 
    p: 4, 
    textAlign: 'center',
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <h2>🚧 {name}</h2>
    <p>Questo componente è in fase di sviluppo</p>
    <button onClick={() => window.history.back()}>
      ← Torna indietro
    </button>
  </Box>
);

// Loading component wrapper con timeout
const LoadingWithTimeout = () => {
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 10000); // 10 secondi
    return () => clearTimeout(timer);
  }, []);

  if (timedOut) {
    return <ComponentNotFound name="Componente" />;
  }

  return <LoadingScreen />;
};

// Home wrapper: courtesy page + redirect utente loggato + multi-tenant
const HomeWrapper = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { shouldShow: shouldShowCourtesyPage, isReady } = useCourtesyPage();
  const { loading: domainLoading } = useDomain();

  if (domainLoading || authLoading || !isReady) return <LoadingScreen />;
  if (shouldShowCourtesyPage) return <CourtesyPage />;
  
  // SEMPLIFICATO: Se utente è loggato E ha un profilo → vai a JustGo
  if (user && userProfile) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <JustGo />
      </Suspense>
    );
  }
  
  return <Home />;
};

// Wrapper per routes protette con controllo multi-tenant
// TEMPORANEAMENTE DISABILITATO ProjectAccessGuard che causa errori
const MultiTenantProtectedRoute = ({ children, ...props }) => (
  <ProtectedRoute {...props}>
    {children}
  </ProtectedRoute>
);

// Wrapper per componenti lazy con Suspense
const LazyRoute = ({ Component, ...props }) => (
  <Suspense fallback={<LoadingWithTimeout />}>
    <Component {...props} />
  </Suspense>
);

// Default theme fallback
const DEFAULT_THEME = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
    },
  },
  typography: { fontFamily: 'Inter, sans-serif' }
});

// AppContent con gestione robusta del tema
const AppContent = () => {
  const domainContext = useDomain();
  
  // Gestione sicura del tema con fallback
  const theme = React.useMemo(() => {
    if (!domainContext) {
      console.warn('DomainContext non disponibile, uso tema default');
      return DEFAULT_THEME;
    }
    
    if (typeof domainContext.buildMuiTheme !== 'function') {
      console.warn('buildMuiTheme non è una funzione, uso tema default');
      return DEFAULT_THEME;
    }
    
    try {
      const themeConfig = domainContext.buildMuiTheme();
      return createTheme(themeConfig);
    } catch (error) {
      console.error('Errore nella creazione del tema:', error);
      return DEFAULT_THEME;
    }
  }, [domainContext]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <MainNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
          <Routes>
            {/* Home pubblica o homepage utente */}
            <Route path="/" element={<HomeWrapper />} />

            {/* Pubbliche (sempre accessibili) */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

            {/* Verifica email (protetta ma senza controllo progetto) */}
            <Route path="/verify-email" element={<ProtectedRoute><VerifyEmailPage /></ProtectedRoute>} />

            {/* Protette con controllo multi-tenant */}
            <Route
              path="/onboarding"
              element={
                <MultiTenantProtectedRoute requireOnboarding={false}>
                  <OnboardingGate>
                    <Onboarding />
                  </OnboardingGate>
                </MultiTenantProtectedRoute>
              }
            />
            <Route path="/profile" element={<MultiTenantProtectedRoute><Profile /></MultiTenantProtectedRoute>} />
            <Route path="/UserDashboard" element={<MultiTenantProtectedRoute><UserDashboard /></MultiTenantProtectedRoute>} />

            {/* Admin Dashboard */}
            <Route path="/AdminDashboard" element={<MultiTenantProtectedRoute adminOnly={true}><AdminDashboard /></MultiTenantProtectedRoute>} />

            {/* === GESTIONE PIATTAFORMA === */}
            
            {/* Gestione Progetti */}
            <Route 
              path="/admin/projects" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={GestioneProgetti} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Dashboard di Sistema */}
            <Route 
              path="/admin/SystemDashboard" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={SystemDashboard} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Dashboard di Progetto */}
            <Route 
              path="/admin/ProjectDashboard" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={ProjectDashboard} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Project Agents Structure - NUOVO ROUTE */}
            <Route 
              path="/admin/project-agents-structure" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={ProjectAgentsStructure} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Gestione Template */}
            <Route 
              path="/admin/system-templates" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={GestioneTemplates} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Gestione Policy/Blueprints Agenti */}
            <Route 
              path="/admin/system-policies" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={GestionePolicySistema} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Gestione Agenti Blueprint Sistema */}
            <Route 
              path="/admin/system-agent-blueprints" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={GestioneAgentiBlueprintSistema} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Gestione Utenti Piattaforma */}
            <Route 
              path="/admin/platform-users" 
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={GestioneUtentiPiattaforma} />
                </MultiTenantProtectedRoute>
              } 
            />

            {/* Agent Creator */}
            <Route
              path="/admin/agent-creator"
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={AgentCreator} />
                </MultiTenantProtectedRoute>
              }
            />

            {/* Agents Builder - Sistema Avanzato di Costruzione Agenti */}
            <Route
              path="/admin/agents-builder"
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={AgentBuilder} />
                </MultiTenantProtectedRoute>
              }
            />

            {/* === NUOVI FRONTEND AI INNOVATIVI === */}
            
            {/* AI Workspace Collaborativo */}
            <Route
              path="/ai/workspace-collaborativo"
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={AIWorkspaceCollaborativo} />
                </MultiTenantProtectedRoute>
              }
            />

            {/* Document Intelligence Studio */}
            <Route
              path="/ai/document-intelligence-studio"
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={DocumentIntelligenceStudio} />
                </MultiTenantProtectedRoute>
              }
            />

            {/* AI Agent Orchestrator */}
            <Route
              path="/ai/agent-orchestrator"
              element={
                <MultiTenantProtectedRoute adminOnly={true}>
                  <LazyRoute Component={AIAgentOrchestrator} />
                </MultiTenantProtectedRoute>
              }
            />

            {/* === UTENTE/AI === */}
            
            {/* Creazione Contenuti */}
            <Route 
              path="/user/create-impronta" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={CreaImpronta} />
                </MultiTenantProtectedRoute>
              } 
            />
            <Route 
              path="/user/create-impronta-up" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={CreaImprontaUp} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* File Manager */}
            <Route 
              path="/user/file-manager" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={FileManager} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Chat Pages */}
            <Route 
              path="/user/chat-page" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={AgentChatPage} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Committee Chat */}
            <Route 
              path="/user/chat-page-committee" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={AgentChatPageCommittee} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* AI Committee Room - WORKSPACE COLLABORATIVO */}
            <Route 
              path="/user/committee-room" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={Aicommitteeroom} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Brainstorming */}
            <Route 
              path="/user/chat-ux" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={Agent_call_ux} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Battle Arena */}
            <Route 
              path="/user/battle-arena" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={AgentBattleArena} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* Archivio */}
            <Route 
              path="/agents/ux/archivio" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={ArchivioRisonanze} />
                </MultiTenantProtectedRoute>
              } 
            />
            
            {/* JustGo */}
            <Route 
              path="/justgo" 
              element={
                <MultiTenantProtectedRoute>
                  <LazyRoute Component={JustGo} />
                </MultiTenantProtectedRoute>
              } 
            />

            {/* 404 - Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

// App principale con providers
const App = () => (
  <Router>
    <DomainProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </DomainProvider>
  </Router>
);

export default App;